import { useEffect } from "react";
import { ComponentQueryLoader } from "../../../../../../../shared/component-query-loader";
import { FileUpload } from "./file-upload";
import {
  AuthResult,
  AzureBlobResponse,
} from "../../../../../../../shared/file-upload/azure-upload";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  CasesCredentialVerificationV1UploadNameOnDocumentContainerApplicantUserFileCreateAuthHeaderDocument,
  CasesCredentialVerificationV1UploadNameOnDocumentContainerApplicantUserDocument,
  CasesCredentialVerificationV1UploadNameOnDocumentContainerApplicantUserUpdateDocument,
  ApplicantUserUpdateInput,
  FileInfo,
} from "../../../../../../../../generated";

interface UploadNameOnDocumentContainerProps {
  title: string;
  description: string;
  buttonText: string;
  applicantUserId: string;
  fileName: string;
  permittedContentTypes: string[];
  permittedExtensions: string[];
  setIsFileUploaded?: (fileName: string, isUploaded: boolean) => void;
  buildUpdateInput?: (
    response: AzureBlobResponse,
    fileName: string
  ) => ApplicantUserUpdateInput;
  fetchFileData?: () => void;
  previewOnly?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  isShowFileName?: boolean;
}

export const UploadNameOnDocumentContainer: React.FC<UploadNameOnDocumentContainerProps> = ({ previewOnly = false, ...props }) => {
  const [updateAssets] = useMutation(
    CasesCredentialVerificationV1UploadNameOnDocumentContainerApplicantUserUpdateDocument
  );
  const [getAuthHeader] = useMutation(
    CasesCredentialVerificationV1UploadNameOnDocumentContainerApplicantUserFileCreateAuthHeaderDocument
  );

  const [getFileData, { data: fileData, loading, error }] = useLazyQuery(
    CasesCredentialVerificationV1UploadNameOnDocumentContainerApplicantUserDocument
  );

  const fetchFileData = () => {
    getFileData({
      variables: {
        applicantUserId: props.applicantUserId,
      },
      fetchPolicy: "network-only",
    });
  };

  useEffect(() => {
    fetchFileData();
  }, [props.fileName]);

  useEffect(() => {
  }, [fileData]);

  const addNewAsset = async (file: File): Promise<AuthResult> => {
    return getAuthHeader({
      variables: {
        input: {
          applicantId: props.applicantUserId,
          fileName: props.fileName,
          contentType: file.type,
          contentLength: file.size,
        },
      },
    })
      .then((response) => {
        return {
          ...response?.data?.applicantUserFileCreateAuthHeader,
          ...{ isAuthorized: true },
        } as AuthResult;
      })
      .catch((error) => {
        console.error("Error uploading asset: ", error);
        return { isAuthorized: false } as AuthResult;
      });
  };

  const onSuccess = async () => {
    await updateAssets({
      variables: {
        input: {
          id: props.applicantUserId,
          applicantUserDetails: {
            personalInformation: {
              nameOnDocumentAssets: [{ documentName: props.fileName }],
            },
          },
        },
      },
    });
  };

  return (
    <ComponentQueryLoader
      loading={loading}
      error={error}
      hasData={fileData?.applicantUser !== null}
      hasDataComponent={
        <FileUpload
          title={props.title}
          description={props.description}
          buttonText={props.buttonText}
          assetUpdateFunction={addNewAsset}
          fileData={
            fileData?.applicantUser?.files?.filter(
              (file) => file?.name.split("/").pop() === props.fileName
            )[0] as FileInfo
          }
          fetchFileData={fetchFileData}
          permittedContentTypes={props.permittedContentTypes}
          permittedExtensions={props.permittedExtensions}
          onSuccess={onSuccess}
          previewOnly={previewOnly}
          setIsEditing={props.setIsEditing}
          uploadPreviewImage={false}
          isShowFileName={props.isShowFileName}
        />
      }
    />
  );
};